export type A2gPlanInterval = "monthly" | "annual" | "unknown";
export type A2gBillingMarket = "global" | "sepa";

const CURRENT_MONTHLY_AMOUNT_DOLLARS = 8.99;
const CURRENT_ANNUAL_AMOUNT_DOLLARS = 59.99;
/**
 * The amounts every public price string is rendered from. `LEGACY_*` below are
 * recognition-only: grandfathered subscribers still on the pre-July-2026 prices.
 * Nothing customer-facing may quote a legacy amount — see
 * `public-offer-copy.test.ts`.
 */
export const PRO_MONTHLY_AMOUNT_DOLLARS = CURRENT_MONTHLY_AMOUNT_DOLLARS;
export const PRO_ANNUAL_AMOUNT_DOLLARS = CURRENT_ANNUAL_AMOUNT_DOLLARS;
export const PRO_ANNUAL_MONTHLY_EQUIVALENT_DOLLARS = CURRENT_ANNUAL_AMOUNT_DOLLARS / 12;
/**
 * Exported so internal reporting can compare against what we used to charge
 * without restating the number. Nothing customer-facing may render it --
 * public-offer-copy.test.ts enforces that.
 */
export const LEGACY_MONTHLY_AMOUNT_DOLLARS = 6.99;
const LEGACY_ANNUAL_AMOUNT_DOLLARS = 49.99;
const CURRENT_MONTHLY_AMOUNT_CENTS = 899;
const CURRENT_ANNUAL_AMOUNT_CENTS = 5999;
const LEGACY_MONTHLY_AMOUNT_CENTS = 699;
const LEGACY_ANNUAL_AMOUNT_CENTS = 4999;

export const LEGACY_PRICE_LABELS = [
  `$${LEGACY_MONTHLY_AMOUNT_DOLLARS.toFixed(2)}`,
  `$${LEGACY_ANNUAL_AMOUNT_DOLLARS.toFixed(2)}`,
] as const;

export const CREDIT_PACK_CREDITS = 10;
export const CREDIT_PACK_PRICE_CENTS = 999;
export const CREDIT_PACK_PRICE_DOLLARS = CREDIT_PACK_PRICE_CENTS / 100;
export const CREDIT_PACK_PRICE_LABEL = `$${CREDIT_PACK_PRICE_DOLLARS.toFixed(2)}`;
export const CREDIT_PACK_EUR_PRICE_LABEL = `€${CREDIT_PACK_PRICE_DOLLARS.toFixed(2)}`;
export const PRO_MONTHLY_EUR_PRICE_LABEL = `€${CURRENT_MONTHLY_AMOUNT_DOLLARS.toFixed(2)}`;
export const PRO_ANNUAL_EUR_PRICE_LABEL = `€${CURRENT_ANNUAL_AMOUNT_DOLLARS.toFixed(2)}`;
export const PRO_ANNUAL_EUR_MONTHLY_EQUIVALENT = `€${(CURRENT_ANNUAL_AMOUNT_DOLLARS / 12).toFixed(2)}`;

export function cleanEnv(value: string | undefined): string | null {
  const cleaned = value?.trim();
  return cleaned ? cleaned : null;
}

export function getCreditPackPriceId(market: A2gBillingMarket = "global"): string {
  return cleanEnv(
    market === "sepa"
      ? process.env.STRIPE_CREDITS_EUR_PRICE_ID
      : process.env.STRIPE_CREDITS_PRICE_ID,
  ) || "";
}

export function getA2gPriceId(
  interval: Exclude<A2gPlanInterval, "unknown">,
  market: A2gBillingMarket = "global",
): string {
  const envValue = market === "sepa"
    ? interval === "annual"
      ? process.env.STRIPE_ANNUAL_EUR_PRICE_ID
      : process.env.STRIPE_MONTHLY_EUR_PRICE_ID
    : interval === "annual"
      ? process.env.STRIPE_ANNUAL_PRICE_ID
      : process.env.STRIPE_PRICE_ID;
  return cleanEnv(envValue) || "";
}

export function sepaCheckoutEnabled(): boolean {
  return cleanEnv(process.env.STRIPE_SEPA_CHECKOUT_ENABLED)?.toLowerCase() === "true";
}

export function euAutomaticTaxEnabled(): boolean {
  return cleanEnv(process.env.STRIPE_EU_AUTOMATIC_TAX_ENABLED)?.toLowerCase() === "true";
}

export function sepaBillingConfigured(): boolean {
  return sepaCheckoutEnabled() && Boolean(
    getCreditPackPriceId("sepa") &&
    getA2gPriceId("monthly", "sepa") &&
    getA2gPriceId("annual", "sepa"),
  );
}

export function getA2gStripePriceIds(): Set<string> {
  return new Set(
    [
      cleanEnv(process.env.STRIPE_PRICE_ID),
      cleanEnv(process.env.STRIPE_ANNUAL_PRICE_ID),
      cleanEnv(process.env.STRIPE_LEGACY_PRICE_ID),
      cleanEnv(process.env.STRIPE_LEGACY_ANNUAL_PRICE_ID),
      cleanEnv(process.env.STRIPE_MONTHLY_EUR_PRICE_ID),
      cleanEnv(process.env.STRIPE_ANNUAL_EUR_PRICE_ID),
    ].filter((id): id is string => Boolean(id)),
  );
}

export function isA2gStripePrice(priceId: string | null | undefined): boolean {
  return !!priceId && getA2gStripePriceIds().has(priceId);
}

export function planFromA2gPrice(priceId: string | null | undefined): A2gPlanInterval {
  if (!priceId) return "unknown";
  if (
    priceId === cleanEnv(process.env.STRIPE_PRICE_ID) ||
    priceId === cleanEnv(process.env.STRIPE_LEGACY_PRICE_ID) ||
    priceId === cleanEnv(process.env.STRIPE_MONTHLY_EUR_PRICE_ID)
  ) {
    return "monthly";
  }
  if (
    priceId === cleanEnv(process.env.STRIPE_ANNUAL_PRICE_ID) ||
    priceId === cleanEnv(process.env.STRIPE_LEGACY_ANNUAL_PRICE_ID) ||
    priceId === cleanEnv(process.env.STRIPE_ANNUAL_EUR_PRICE_ID)
  ) {
    return "annual";
  }
  return "unknown";
}

export function getA2gPlanAmountDollars(priceId: string | null | undefined): number | null {
  if (!priceId) return null;
  if (priceId === cleanEnv(process.env.STRIPE_PRICE_ID)) return CURRENT_MONTHLY_AMOUNT_DOLLARS;
  if (priceId === cleanEnv(process.env.STRIPE_ANNUAL_PRICE_ID)) return CURRENT_ANNUAL_AMOUNT_DOLLARS;
  if (priceId === cleanEnv(process.env.STRIPE_MONTHLY_EUR_PRICE_ID)) return CURRENT_MONTHLY_AMOUNT_DOLLARS;
  if (priceId === cleanEnv(process.env.STRIPE_ANNUAL_EUR_PRICE_ID)) return CURRENT_ANNUAL_AMOUNT_DOLLARS;
  if (priceId === cleanEnv(process.env.STRIPE_LEGACY_PRICE_ID)) return LEGACY_MONTHLY_AMOUNT_DOLLARS;
  if (priceId === cleanEnv(process.env.STRIPE_LEGACY_ANNUAL_PRICE_ID)) return LEGACY_ANNUAL_AMOUNT_DOLLARS;
  return null;
}

/**
 * Billing interval implied by a charge amount, for charges we can't read price
 * ids from. Stripe removed `invoice` from the Charge object (gone as of API
 * 2026-03-25.dahlia), so subscription charges arrive with no line items and the
 * amount is the only signal left. Safe here because the monthly amounts
 * (899/699) and annual amounts (5999/4999) don't collide — EUR prices reuse the
 * same numbers in a different currency but keep the same interval.
 *
 * Returns "unknown" for anything that isn't a recognised plan amount (e.g. a
 * mid-cycle proration), rather than guessing.
 */
export function a2gIntervalFromAmountCents(
  amountCents: number | null | undefined,
): A2gPlanInterval {
  if (typeof amountCents !== "number" || !Number.isFinite(amountCents)) return "unknown";
  if (amountCents === CURRENT_MONTHLY_AMOUNT_CENTS || amountCents === LEGACY_MONTHLY_AMOUNT_CENTS) {
    return "monthly";
  }
  if (amountCents === CURRENT_ANNUAL_AMOUNT_CENTS || amountCents === LEGACY_ANNUAL_AMOUNT_CENTS) {
    return "annual";
  }
  return "unknown";
}

export function isA2gSubscriptionAmountCents(amountCents: number | null | undefined): boolean {
  if (typeof amountCents !== "number" || !Number.isFinite(amountCents)) return false;

  return new Set([
    CURRENT_MONTHLY_AMOUNT_CENTS,
    CURRENT_ANNUAL_AMOUNT_CENTS,
    LEGACY_MONTHLY_AMOUNT_CENTS,
    LEGACY_ANNUAL_AMOUNT_CENTS,
  ]).has(amountCents);
}

export function getA2gBillingConfigHealth(): {
  missingPriceEnv: string[];
  hasLegacyPrices: boolean;
  creditPackPriceConfigured: boolean;
  missingCreditPackPriceEnv: string[];
  sepaCheckoutEnabled: boolean;
  sepaConfigured: boolean;
  missingSepaPriceEnv: string[];
  euAutomaticTaxEnabled: boolean;
} {
  const missingPriceEnv = [
    ["STRIPE_PRICE_ID", cleanEnv(process.env.STRIPE_PRICE_ID)],
    ["STRIPE_ANNUAL_PRICE_ID", cleanEnv(process.env.STRIPE_ANNUAL_PRICE_ID)],
  ]
    .filter(([, value]) => !value)
    .map(([name]) => name)
    .filter((name): name is string => Boolean(name));
  const creditPackPriceConfigured = Boolean(getCreditPackPriceId());
  const missingSepaPriceEnv = [
    ["STRIPE_CREDITS_EUR_PRICE_ID", getCreditPackPriceId("sepa")],
    ["STRIPE_MONTHLY_EUR_PRICE_ID", getA2gPriceId("monthly", "sepa")],
    ["STRIPE_ANNUAL_EUR_PRICE_ID", getA2gPriceId("annual", "sepa")],
  ]
    .filter(([, value]) => !value)
    .map(([name]) => name)
    .filter((name): name is string => Boolean(name));

  return {
    missingPriceEnv,
    hasLegacyPrices: Boolean(
      cleanEnv(process.env.STRIPE_LEGACY_PRICE_ID) &&
        cleanEnv(process.env.STRIPE_LEGACY_ANNUAL_PRICE_ID),
    ),
    creditPackPriceConfigured,
    missingCreditPackPriceEnv: creditPackPriceConfigured ? [] : ["STRIPE_CREDITS_PRICE_ID"],
    sepaCheckoutEnabled: sepaCheckoutEnabled(),
    sepaConfigured: sepaBillingConfigured(),
    missingSepaPriceEnv,
    euAutomaticTaxEnabled: euAutomaticTaxEnabled(),
  };
}
